import { useCallback, useEffect, useRef, useState } from 'react';
import { GlobalSearchGroupDto } from 'api/search';
import { searchAPI } from 'services/swagger-api';

const DEBOUNCE_MS = 300;
const MIN_CHARS = 2;
const EXPANDED_LIMIT = 30;

/**
 * Recherche globale du header : debounce 300 ms, appel à partir de 2 caractères utiles,
 * top 5 par groupe, puis « voir plus » = re-appel ciblé sur un type (limite 30).
 */
export const useGlobalSearch = () => {
  const [searchText, setSearchText] = useState('');
  const [groups, setGroups] = useState<GlobalSearchGroupDto[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [hasSearched, setHasSearched] = useState(false);
  const [expandedTypes, setExpandedTypes] = useState<Set<string>>(new Set());
  // Ignore les réponses obsolètes (l'utilisateur a continué à taper)
  const requestSeq = useRef(0);

  const trimmed = searchText.trim();
  const isActive = trimmed.length >= MIN_CHARS;

  useEffect(() => {
    if (!isActive) {
      setGroups([]);
      setHasSearched(false);
      setIsLoading(false);
      return;
    }

    const seq = ++requestSeq.current;
    setIsLoading(true);
    const handle = setTimeout(async () => {
      try {
        const response = await searchAPI.globalSearch({
          globalSearchCommand: { searchString: trimmed }
        });
        if (seq !== requestSeq.current) return;
        setGroups(response.groups ?? []);
        setExpandedTypes(new Set());
        setHasSearched(true);
      } catch (error) {
        if (seq !== requestSeq.current) return;
        console.error('Erreur recherche globale:', error);
        setGroups([]);
        setHasSearched(true);
      } finally {
        if (seq === requestSeq.current) setIsLoading(false);
      }
    }, DEBOUNCE_MS);

    return () => clearTimeout(handle);
  }, [trimmed, isActive]);

  /** « Voir les N résultats » : recharge un seul groupe avec une limite élargie. */
  const expandGroup = useCallback(
    async (type: string) => {
      try {
        const response = await searchAPI.globalSearch({
          globalSearchCommand: {
            searchString: trimmed,
            types: [type],
            limitPerType: EXPANDED_LIMIT
          }
        });
        const expanded = response.groups?.find((g) => g.type === type);
        if (!expanded) return;
        setGroups((prev) => prev.map((g) => (g.type === type ? expanded : g)));
        setExpandedTypes((prev) => new Set(prev).add(type));
      } catch (error) {
        console.error('Erreur « voir plus » recherche globale:', error);
      }
    },
    [trimmed]
  );

  const reset = useCallback(() => {
    setSearchText('');
    setGroups([]);
    setHasSearched(false);
    setExpandedTypes(new Set());
  }, []);

  return {
    searchText,
    setSearchText,
    groups,
    isLoading,
    /** Vrai quand une recherche a répondu (permet d'afficher « aucun résultat »). */
    hasSearched,
    /** Vrai à partir de 2 caractères utiles (le dropdown ne s'ouvre qu'alors). */
    isActive,
    expandedTypes,
    expandGroup,
    reset
  };
};
